/**
 * What a hotspot click does with the camera. Shared by createViewer's click path and the tests.
 */
import type { ExtraWaypointData, HotspotData, HotspotOrbitView } from '../types';
import { findExtraWaypoint } from './extraWaypoints';

export interface HotspotViewMoveContext {
    /** A VR / AR session owns the camera. */
    isInXR: boolean;
    /** The viewer runs inside the StorySplat editor. */
    isEditorMode: boolean;
    /** The hotspot the editor has open in its hotspot editor (gizmo attached), if any. */
    editingHotspotId: string | null;
}

/**
 * Whether a click on `hotspotId` may move the camera to a view (its orbit view or an extra
 * waypoint). Not in XR, where the headset owns the camera. In the editor it may, as in the
 * published scene, except for the hotspot open in the hotspot editor: its gizmo is there, so a
 * click on it is about editing it (or previewing its popup), not about leaving it.
 */
export function hotspotClickMayMoveCamera(hotspotId: string | undefined, context: HotspotViewMoveContext): boolean {
    if (context.isInXR) return false;
    if (context.isEditorMode && context.editingHotspotId !== null && context.editingHotspotId === hotspotId) return false;
    return true;
}

/** The camera action a hotspot click runs, in precedence order (see resolveHotspotCameraAction). */
export type HotspotCameraAction =
    | { kind: 'waypoint'; index: number }
    | { kind: 'percent'; percent: number }
    | { kind: 'extraWaypoint'; waypoint: ExtraWaypointData }
    | { kind: 'orbitView'; view: HotspotOrbitView }
    | { kind: 'none' };

/**
 * What a hotspot click does with the camera. One action, first match wins:
 * 1. teleportWaypoint (or its legacy alias): the tour, at that waypoint;
 * 2. teleportPercent (or its alias): the tour, at that scroll percentage;
 * 3. teleportExtraWaypoint: fly to that extra waypoint in explore mode (an id with no extra
 *    waypoint, e.g. a deleted one, is skipped);
 * 4. orbitView: travel to the hotspot's own orbit view in explore mode.
 * -1 (or absent) means "not set" for the first two, as before.
 */
export function resolveHotspotCameraAction(
    hotspot: Pick<HotspotData, 'teleportWaypoint' | 'teleportToWaypoint' | 'teleportPercent' | 'teleportToPercent' | 'teleportExtraWaypoint' | 'orbitView'>,
    extraWaypoints: readonly ExtraWaypointData[] | undefined,
): HotspotCameraAction {
    const teleportWaypoint = hotspot.teleportWaypoint ?? hotspot.teleportToWaypoint;
    if (teleportWaypoint !== undefined && teleportWaypoint !== -1) return { kind: 'waypoint', index: teleportWaypoint };
    const teleportPercent = hotspot.teleportPercent ?? hotspot.teleportToPercent;
    if (teleportPercent !== undefined && teleportPercent !== -1) return { kind: 'percent', percent: teleportPercent };
    const extra = findExtraWaypoint(extraWaypoints, hotspot.teleportExtraWaypoint);
    if (extra) return { kind: 'extraWaypoint', waypoint: extra };
    const view = hotspot.orbitView;
    if (view?.cameraPosition && view.pivotPoint) return { kind: 'orbitView', view };
    return { kind: 'none' };
}

type Point = { x: number; y: number; z: number };

/**
 * Where the camera goes to bring a hotspot into view when a visitor picks it in the directory and
 * it has no camera action of its own: the orbit view around the marker, seen from the side the
 * camera is on now (a short move that ends facing the marker). A camera already at a comfortable
 * distance keeps it; one too close or too far comes to 3 to 15 marker sizes away (at least 0.5 and
 * at most 3 scene units for small markers), within the orbit zoom range. `away` is the direction
 * used when the camera sits on the marker. Null when the positions aren't finite.
 */
export function hotspotIntoView(
    marker: Point,
    camera: Point,
    markerSize: number,
    zoomRange: { min: number; max: number } = { min: 0, max: Infinity },
    away: Point = { x: 0, y: 0, z: 1 },
): { position: Point; pivot: Point } | null {
    const finite = (p: Point) => Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z);
    if (!finite(marker) || !finite(camera)) return null;
    const size = Number.isFinite(markerSize) && markerSize > 0 ? markerSize : 0.2;
    let dir = { x: camera.x - marker.x, y: camera.y - marker.y, z: camera.z - marker.z };
    let distance = Math.hypot(dir.x, dir.y, dir.z);
    if (distance < 1e-6) {
        dir = finite(away) && Math.hypot(away.x, away.y, away.z) > 1e-6 ? away : { x: 0, y: 0, z: 1 };
        distance = 0;
    }
    const length = Math.hypot(dir.x, dir.y, dir.z);
    const unit = { x: dir.x / length, y: dir.y / length, z: dir.z / length };
    const near = Math.max(0.5, size * 3);
    const far = Math.max(3, size * 15);
    const target = Math.min(Math.max(Math.min(far, Math.max(near, distance)), zoomRange.min), zoomRange.max);
    return {
        position: { x: marker.x + unit.x * target, y: marker.y + unit.y * target, z: marker.z + unit.z * target },
        pivot: { ...marker },
    };
}
